import { Loader2, LockKeyhole } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { TERRITORIES_TOUR_CLASSES } from '@/constants/tour/classNames';
import { toast } from '@/hooks/ui/use-toast';
import useZorMeetingLinkStore from '@/stores/useZorMeetingLinkStore';

interface TerritoryReserveDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  loading: boolean;
}

const TerritoryReserveDialog = ({
  isOpen,
  onOpenChange,
  onConfirm,
  loading,
}: TerritoryReserveDialogProps) => {
  const { meetingLink } = useZorMeetingLinkStore();

  const handleBookCallClick = () => {
    if (!meetingLink) {
      toast({
        title: 'No meeting link available',
        description: 'Please contact support or try again later.',
        variant: 'destructive',
      });
      return;
    }

    // If not empty, open link
    window.open(meetingLink, '_blank');
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={loading}
          onClick={() => {}}
          className={TERRITORIES_TOUR_CLASSES.TERRITORY_RESERVE_BUTTON}
        >
          <LockKeyhole className="size-4" /> Request reserve
        </Button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        className="z-[9999] w-[300px] space-y-4 rounded-lg border border-border bg-white p-4"
      >
        <div className="space-y-1">
          <p className="as-title-02 as-strong text-gray-700">
            Reserve Your Territory
          </p>
          <p className="as-title-04 as-strong text-black">
            You can only reserve one territory at a time.
          </p>
          <p className="as-title-04 text-muted-foreground">
            To proceed, you must be ready to book a site visit within 5
            days—otherwise, the territory will be reassigned.
          </p>
          <p className="as-title-04">
            <span
              className="cursor-pointer text-primary underline"
              onClick={handleBookCallClick}
            >
              Book a Call
            </span>
            {'  '}
            now to secure your spot!
          </p>
        </div>
        <div className="flex items-center justify-between border-t border-border pt-4">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={onConfirm}
            disabled={loading}
          >
            {loading && <Loader2 className="size-4 animate-spin" />}
            Proceed
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { TerritoryReserveDialog };
